// Contact facts shared by the contacts page and the inbox's customer panel:
// tags and consent. Who may change what is enforced by the server; the
// flags only hide controls the server would refuse.
import { useState, type FormEvent } from 'react'
import { optInLabel, useUpdateContact, type Contact } from '../api/contacts'
import { useContactTag, useTags } from '../api/tags'
import { RequestFailed } from '../api/client'
import { fullTime } from '../lib/format'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Label } from '@/components/ui/label'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Switch } from '@/components/ui/switch'
import { Textarea } from '@/components/ui/textarea'

export function ContactTagEditor({ c, canTag }: { c: Contact; canTag: boolean }) {
  const tags = useTags()
  const change = useContactTag(c.id)
  const on = new Set(c.tags.map((t) => t.id))
  const addable = (tags.data ?? []).filter((t) => !on.has(t.id))
  return (
    <div className="flex flex-wrap items-center gap-1.5">
      {c.tags.length === 0 && <span className="text-sm text-muted-foreground">No tags.</span>}
      {c.tags.map((t) => (
        <Badge key={t.id} variant="outline" className="gap-0.5 pr-0.5">
          {t.name}
          {canTag && (
            <button
              className="flex size-5 items-center justify-center rounded-full text-muted-foreground hover:bg-accent hover:text-foreground"
              aria-label={`Remove tag ${t.name}`}
              disabled={change.isPending}
              onClick={() => change.mutate({ tagId: t.id, on: false })}
            >
              <span aria-hidden="true">×</span>
            </button>
          )}
        </Badge>
      ))}
      {canTag && addable.length > 0 && (
        <Select key={c.tags.length} disabled={change.isPending} onValueChange={(v) => change.mutate({ tagId: v, on: true })}>
          <SelectTrigger size="sm" aria-label="Add a tag" className="h-7 border-dashed text-xs text-primary-text">
            <SelectValue placeholder="Add tag" />
          </SelectTrigger>
          <SelectContent>
            {addable.map((t) => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}
          </SelectContent>
        </Select>
      )}
      {change.error && <p role="alert" className="basis-full text-sm text-destructive">{(change.error as RequestFailed).message}</p>}
    </div>
  )
}

export function Consent({ c, admin }: { c: Contact; admin: boolean }) {
  const update = useUpdateContact(c.id)
  const [proof, setProof] = useState('')
  const [recording, setRecording] = useState(false)
  const error = update.error as RequestFailed | null
  const source = { '': '', import: 'from a CSV import', manual: 'recorded by your team', stop: 'the customer wrote STOP' }[c.optInSource]

  return (
    <section className="flex flex-col gap-3 rounded-xl border bg-card p-4">
      <div className="flex flex-col gap-1">
        <h3 className="text-sm font-semibold">Consent to business-initiated messages</h3>
        <p className="text-sm">
          <span className="font-medium">{optInLabel[c.optIn]}</span>
          {c.optInAt && source ? `, ${source} on ${fullTime(c.optInAt)}.` : '.'}
        </p>
        {c.optInProof && <p className="text-sm text-muted-foreground [overflow-wrap:anywhere]">Proof: {c.optInProof}</p>}
        <p className="text-xs text-muted-foreground">Meta requires opt-in before you message a customer first. Replies inside the 24 hour window never need it.</p>
      </div>
      {admin && c.optIn !== 'optedIn' && !recording && (
        <Button variant="outline" size="sm" className="w-fit" onClick={() => setRecording(true)}>Record opt-in</Button>
      )}
      {admin && recording && (
        <form
          className="flex flex-col gap-2"
          onSubmit={(e: FormEvent) => {
            e.preventDefault()
            update.mutate({ optIn: 'optedIn', optInProof: proof.trim() }, { onSuccess: () => { setRecording(false); setProof('') } })
          }}
        >
          <Label htmlFor="opt-in-proof">How did the customer agree?</Label>
          <Textarea id="opt-in-proof" rows={2} maxLength={500} placeholder="Ticked the WhatsApp box on the order form, 2 Nov 2026" value={proof} onChange={(e) => setProof(e.target.value)} />
          <div className="flex gap-2">
            <Button type="submit" size="sm" disabled={!proof.trim() || update.isPending}>Save consent</Button>
            <Button type="button" size="sm" variant="ghost" onClick={() => setRecording(false)}>Cancel</Button>
          </div>
        </form>
      )}
      {admin && c.optIn === 'optedIn' && (
        <Button variant="ghost" size="sm" className="w-fit text-destructive hover:text-destructive" disabled={update.isPending} onClick={() => update.mutate({ optIn: 'optedOut' })}>
          Record opt-out
        </Button>
      )}
      <div className="flex items-center justify-between gap-3 border-t pt-3">
        <div className="flex flex-col">
          <Label htmlFor="suppressed" className="text-sm">Do not message</Label>
          <span className="text-xs text-muted-foreground">
            {c.suppressed
              ? c.suppressionReason === 'stop' ? 'The customer asked you to stop. Campaigns skip this contact.' : 'Set by your team. Campaigns skip this contact.'
              : 'Campaigns may include this contact when they have opted in.'}
          </span>
        </div>
        <Switch id="suppressed" checked={c.suppressed} disabled={!admin || update.isPending} onCheckedChange={(on) => update.mutate({ suppressed: on })} />
      </div>
      {error && <p role="alert" className="text-sm text-destructive">{error.message}</p>}
    </section>
  )
}
